import { capitalize, formatISODate } from '../../../../../../common/utils/common.util';
import { BudgetSummaryDTO, DrawSummary, ProjectSummary } from '../../../../../project/project.type';
import { SiteConditionsDTO } from '../../../types/monitoring-report.types';
import { CoverPageDTO } from '../../../../magic-report/magic-report.type';
import { orEmpty, joinNonEmpty, possessive } from '../../../../../../common/utils/textFormat.util';
import { roundCompletionPercent } from '../../../utils/percent.util';

// Generic interface for report data needed by templates
export interface ReportTemplateData {
  projectSummary?: Partial<ProjectSummary>;
  drawSummary?: Partial<DrawSummary>;
  budgetSummary?: Partial<BudgetSummaryDTO>;
  siteConditions?: SiteConditionsDTO;
  coverPage?: CoverPageDTO | null;
  /** Magic report sections (used to pull the Project Description free-text field). Optional — unused for limited/quarterly reports. */
  sections?: any;
  author?: {
    name?: string;
    signatureUrl?: string;
    email?: string;
  };
}

/**
 * Formats a currency value to $xx,xxx,xxx.xx format
 * Always rounds to nearest penny (2 decimal places) and includes commas
 * @param value - The amount to format (number, string, or placeholder string)
 * @returns Formatted currency string or original placeholder if value is a placeholder
 */
const formatCurrencyAmount = (value: number | string | undefined | null): string => {
  // If it's a placeholder string, return as is
  if (typeof value === 'string' && (value.startsWith('[') || value === '[requested amount]')) {
    return value;
  }

  // Parse the value to a number
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  // If not a valid number, return placeholder
  if (numValue === null || numValue === undefined || isNaN(numValue)) {
    return '[requested amount]';
  }

  // Round to nearest penny (2 decimal places)
  const rounded = Math.round(numValue * 100) / 100;

  // Format with commas and always 2 decimal places
  const formatted = rounded.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `$ ${formatted}`;
};

export const escapeHtml = (value: unknown): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

type TotalRowLabel = 'coverLetter' | 'executiveSummary';

type BudgetRow = NonNullable<CoverPageDTO['budgetRows']>[number];

const buildWhoPhrase = (contractor: string, contractName: string, application: string | number): string => {
  const forWhom = joinNonEmpty([possessive(contractor), orEmpty(contractName)], ' ');
  const safeApplication = application || '[application number]';
  const applicationPart = `Application #${safeApplication}`;
  return joinNonEmpty([forWhom, applicationPart], ' ');
};

/**
 * The "$X for <who>" phrase for one amount line.
 *
 * Rows seeded from a covered draw carry their own contractor / contract / application number, so a
 * multi-draw report reads "$282,569.81 for Buffalo Construction's Lightbridge Application #4" on
 * every draw line. Rows the user added by hand (e.g. "Soft Costs") just use their name. Rows saved
 * before multi-draw carry none of these, so they fall back to the report-level trio and print
 * exactly as they did before. Missing pieces are omitted (not shown as bracket placeholders).
 */
const buildAmountLineLabel = (row: BudgetRow, index: number, contractor: string, contractName: string, application: string | number) => {
  if (row.drawId || row.contractName || row.applicationNumber) {
    const rowContractor = escapeHtml(row.contractor || contractor);
    const rowContractName = escapeHtml(row.contractName || row.budgetName || contractName);
    const rowApplication = escapeHtml(row.applicationNumber ?? application);
    return buildWhoPhrase(rowContractor, rowContractName, rowApplication);
  }
  if (index === 0) {
    return buildWhoPhrase(escapeHtml(contractor), escapeHtml(contractName), escapeHtml(application));
  }
  return escapeHtml(row.budgetName);
};

/** Which amount each printed list shows: the cover letter states what we recommend releasing,
 * while §1.1 Application for Payment states what the contractor requested. */
type AmountField = 'amountRecommendedForFunding' | 'requestedAmount';

const buildAmountLinesHtml = (
  budgetRows: CoverPageDTO['budgetRows'] | undefined,
  fallbackAmount: number | undefined,
  contractor: string,
  contractName: string,
  application: string | number,
  totalRowLabel: TotalRowLabel,
  amountField: AmountField = 'amountRecommendedForFunding',
): string => {
  const line = (amount: number | undefined, label: string) =>
    `<p style="text-align: center;"><strong>${joinNonEmpty([formatCurrencyAmount(amount), label && `for ${label}`], ' ')}</strong></p>`;

  if (!budgetRows || budgetRows.length === 0) {
    return line(fallbackAmount, buildWhoPhrase(escapeHtml(contractor), escapeHtml(contractName), escapeHtml(application)));
  }

  const lines = budgetRows
    .map((row, index) => line(row[amountField], buildAmountLineLabel(row, index, contractor, contractName, application)))
    .join('');

  if (budgetRows.length === 1) return lines;

  const total = formatCurrencyAmount(budgetRows.reduce((sum, r) => sum + (r[amountField] || 0), 0));
  const totalLine =
    totalRowLabel === 'coverLetter'
      ? `<p style="text-align: center;"><strong>${total} for the Total Loan Draw</strong></p>`
      : `<p style="text-align: center;"><strong>Total Loan Draw of ${total}</strong></p>`;
  return lines + totalLine;
};

// Rows with a blank/null percentComplete are omitted; 0 and any other value are rendered.
const buildPercentCompleteLine = (percentage: number, contractName: string): string => {
  const forThe = contractName ? `for the ${contractName} Contract` : '';
  return `<p style="text-align: center; margin-bottom: 0;"><strong>${joinNonEmpty([`${percentage}% Complete`, forThe], ' ')}</strong></p>`;
};

const buildPercentCompleteLinesHtml = (
  budgetRows: CoverPageDTO['budgetRows'] | undefined,
  fallbackPercent: number | undefined,
  contractName: string,
): string => {
  const safeContractName = escapeHtml(orEmpty(contractName));
  if (!budgetRows || budgetRows.length === 0) {
    const percentage = roundCompletionPercent(fallbackPercent || 0);
    return buildPercentCompleteLine(percentage, safeContractName);
  }

  const lines = budgetRows
    .map((row, index) => {
      if (row.percentComplete === null || row.percentComplete === undefined) return null;
      const percentage = roundCompletionPercent(row.percentComplete);
      // Draw-backed rows name their own contract ("the Dutch Bros Contract"); legacy row 0 uses
      // the report-level contract name (omitted entirely when missing); hand-added rows use their
      // label as typed.
      if (row.drawId || row.contractName) {
        const rowContractName = escapeHtml(row.contractName || row.budgetName);
        return buildPercentCompleteLine(percentage, rowContractName);
      }
      if (index === 0) return buildPercentCompleteLine(percentage, safeContractName);
      const label = escapeHtml(row.budgetName);
      return `<p style="text-align: center; margin-bottom: 0;"><strong>${percentage}% Complete for ${label}</strong></p>`;
    })
    .filter((line): line is string => line !== null);

  if (lines.length === 0) {
    const percentage = roundCompletionPercent(fallbackPercent || 0);
    return buildPercentCompleteLine(percentage, safeContractName);
  }
  return lines.join('');
};

export interface ExecSummaryReductions {
  rows: Array<{
    description: string;
    requestedAmount: number;
    percentRequested: number;
    percentRecommended: number;
    fundingAmount: number;
    reducedBy: number;
  }>;
  zeroWorkComments: string;
  partialReductionComments: string;
  requestedAmount: number | undefined;
  recommendedAmount: number | undefined;
}

export const buildReductionCommentsHtml = (reductions: ExecSummaryReductions): string => {
  const allComments: string[] = [];
  const addComments = (text: string) => {
    if (!text) return;
    text
      .split(/\n\n+/)
      .map(s => s.trim())
      .filter(Boolean)
      .forEach(s => allComments.push(s));
  };
  addComments(reductions.zeroWorkComments);
  addComments(reductions.partialReductionComments);
  return allComments
    .map((comment, i) => {
      const renumbered = comment.replace(/^\d+[-.]?\s*/, `${i + 1}. `);
      const escaped = renumbered.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br />');
      return `<p style="margin: 0 0 0.5em 0; padding-left: 1.5em;">${escaped}</p>`;
    })
    .join('');
};

export const buildReductionTableHtml = (rows: ExecSummaryReductions['rows']): string => {
  const fmtAmt = (v: number) => `$ ${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtPct = (v: number) => `${Math.round(v)}%`;
  // Round the recommended percent down to 99% when it would otherwise round up to 100% alongside
  // a requested percent that also rounds to 100% — this keeps the recommendation visibly reduced.
  const fmtRecommendedPct = (recommended: number, requested: number) => {
    const rounded = Math.round(recommended);
    if (rounded >= 100 && recommended < 100 && Math.round(requested) >= 100) return '99%';
    return `${rounded}%`;
  };
  const cell = (content: string, right = false, bold = false) =>
    `<td style="border:none;padding:4px 6px;font-size: 16px;${right ? 'text-align:right;' : ''}${bold ? 'font-weight:bold;' : ''}">${content}</td>`;
  const dataRows = rows
    .map(
      r => `<tr>
        ${cell(r.description || '--')}
        ${cell(fmtAmt(r.requestedAmount), true)}
        ${cell(fmtPct(r.percentRequested), true)}
        ${cell(fmtRecommendedPct(r.percentRecommended, r.percentRequested), true)}
        ${cell(fmtAmt(r.fundingAmount), true)}
        ${cell(fmtAmt(r.reducedBy), true)}
      </tr>`,
    )
    .join('');
  const totReq = rows.reduce((s, r) => s + (r.requestedAmount || 0), 0);
  const totFund = rows.reduce((s, r) => s + (r.fundingAmount || 0), 0);
  const totRed = rows.reduce((s, r) => s + (r.reducedBy || 0), 0);
  const th = (label: string, right = false) =>
    `<th style="border:none;padding:4px 6px;font-size: 16px;font-weight:bold;${right ? 'text-align:right;' : 'text-align:left;'}">${label}</th>`;
  return `
    <table style="width:100%;border-collapse:collapse;border:none;margin:0.5em 0;">
      <thead><tr>
        ${th('Line Item')}${th('Requested Amount', true)}${th('% Complete Requested', true)}${th('% Complete Recommended', true)}${th('Funding Amount', true)}${th('Reduced By', true)}
      </tr></thead>
      <tbody>
        ${dataRows}
        <tr>
          ${cell('Total:', false, true)}
          ${cell(fmtAmt(totReq), true, true)}
          ${cell('', false, true)}
          ${cell('', false, true)}
          ${cell(fmtAmt(totFund), true, true)}
          ${cell(fmtAmt(totRed), true, true)}
        </tr>
      </tbody>
    </table>
  `;
};

/** One draw's reductions, tagged with the names used in that draw's printed paragraph. */
export interface DrawReductions extends ExecSummaryReductions {
  drawId?: string;
  contractName: string;
  contractor: string;
}

export const buildExecutiveSummaryReductionsHtml = (
  reductions: ExecSummaryReductions,
  contractName: string,
  contractor: string,
): string => {
  const onTheApplication = contractName ? `on the ${contractName} application` : 'on this application';
  const reducedWhoseApplication = possessive(contractor) ? `${possessive(contractor)} application` : 'the application';
  return `
  <p><strong>The amount requested ${onTheApplication} is NOT comparable to the Work completed on site in our opinion as explained below. We have reduced ${reducedWhoseApplication} as follows:</strong></p>
  <p style="text-align:center;"><strong>Requested: ${formatCurrencyAmount(reductions.requestedAmount)}</strong></p>
  <p style="text-align:center;"><strong>Recommended: ${formatCurrencyAmount(reductions.recommendedAmount)}</strong></p>
  <p>We have reduced the current funding amount for the following reasons.</p>
  ${buildReductionCommentsHtml(reductions)}
  <p>Please note that the reduction calculations above include adjustments in the Retainage. Refer to our G702 Review Form, attached, for details.</p>
  ${
    reductions.rows.length > 0
      ? `
  <p>We have reduced the individual line items of the current request as follows:</p>
  ${buildReductionTableHtml(reductions.rows)}
  `
      : ''
  }
`;
};

/**
 * One reduction block per draw that has reductions, each naming its own contract and contractor.
 * Reason numbering restarts at 1 inside every block (buildReductionCommentsHtml renumbers per call).
 */
export const buildAllReductionBlocksHtml = (reductions: DrawReductions[]): string =>
  reductions.map(r => buildExecutiveSummaryReductionsHtml(r, escapeHtml(r.contractName), escapeHtml(r.contractor))).join('');

/**
 * Renders the "Project Description" free-text block that appears just below the Site Visit line
 * and above the "1. EXECUTIVE SUMMARY:" heading. Wrapped in a marker div so export-time logic can
 * reliably strip and re-insert a fresh copy even when the executive summary HTML was previously saved.
 * Returns an empty string (renders nothing) when the text is blank.
 */
export const buildProjectDescriptionHtml = (text?: string): string => {
  const v = (text || '').trim();
  if (!v) return '';
  const escaped = escapeHtml(v).replace(/\n/g, '<br />');
  return `<div class="project-description-block"><h3 class="executive-summary-subheading" style="font-weight: bold; margin-bottom: 0.5rem; font-size: 23px !important;">Project Description</h3><p>${escaped}</p></div>`;
};

// Build base Executive Summary HTML matching editor defaults
export const buildExecutiveSummaryHTML = (
  data: ReportTemplateData,
  reportType?: 'limited' | 'magic',
  /** A single draw's reductions, or one entry per covered draw that has reductions. */
  reductions?: ExecSummaryReductions | DrawReductions[],
): string => {
  const contractor = orEmpty(data.projectSummary?.contractor);
  const application = orEmpty(data.drawSummary?.applicationNumber);
  const isMonitoringReportWithSiteConditions = reportType === 'magic' || reportType === 'limited';

  // Build site conditions section using state (dates formatted for display: Month Day, Year)
  const siteConditions = data.siteConditions || {};
  const inspectionDateFormatted = siteConditions.inspectionDate ? formatISODate(siteConditions.inspectionDate) : '';
  const reportDateFormatted = siteConditions.reportDate ? formatISODate(siteConditions.reportDate) : '';
  const siteVisitDateFormatted = inspectionDateFormatted || reportDateFormatted;
  const observerName = joinNonEmpty([data?.projectSummary?.observer?.firstName, data?.projectSummary?.observer?.lastName], ' ');
  const contractName = orEmpty(data.drawSummary?.name);
  const hasReductions = Array.isArray(reductions) ? reductions.length > 0 : Boolean(reductions);

  const siteVisitRow =
    isMonitoringReportWithSiteConditions && Object.keys(siteConditions).length > 0
      ? `
          <tr style="vertical-align: top;">
            <td style="padding: 0 1.5rem 0 0; white-space: nowrap;">Site Visit:</td>
            <td>${siteConditions.siteVisit ? `#${escapeHtml(siteConditions.siteVisit)}` : ''}${siteConditions.siteVisit && siteVisitDateFormatted ? ', ' : ''}${siteVisitDateFormatted ? `${escapeHtml(siteVisitDateFormatted)}` : ''}</td>
          </tr>
      `
      : '';

  return `
    <div class="executive-summary" style="font-family: Times New Roman, serif; font-size: 20px;">
      <div style="text-align: center; margin-bottom: 2rem;">
        <img alt="CD Construction Consulting" class="company-logo" />
      </div>
      <h3 class="executive-summary-subheading" style="font-weight: bold; margin-bottom: 0.5rem; font-size: 20px !important;">CONSTRUCTION MONITORING PROGRESS REPORT</h3>
      <table style="border-collapse: collapse; margin-bottom: 1rem; font-family: Times New Roman, serif; font-size: 20px;">
        <tbody>
          <tr style="vertical-align: top;">
            <td style="padding: 0 1.5rem 0 0; white-space: nowrap;">Project:</td>
            <td><strong>${joinNonEmpty(
              [
                escapeHtml(orEmpty(data?.projectSummary?.projectTitle)),
                joinNonEmpty(
                  [
                    data?.projectSummary?.projectAddress?.street,
                    capitalize(data?.projectSummary?.projectAddress?.city),
                    (data?.projectSummary?.projectAddress?.state || '').toUpperCase(),
                    data?.projectSummary?.projectAddress?.postalCode,
                  ].map(escapeHtml),
                ),
              ],
              ', ',
            )}</strong></td>
          </tr>
          <tr style="vertical-align: top;">
            <td style="padding: 0 1.5rem 0 0; white-space: nowrap;">Project #:</td>
            <td><strong>${escapeHtml(orEmpty(data?.projectSummary?.projectNumber))}</strong></td>
          </tr>
          <tr style="vertical-align: top;">
            <td style="padding: 0 1.5rem 0 0; white-space: nowrap;">Observer:</td>
            <td>${escapeHtml(observerName)}</td>
          </tr>
          ${siteVisitRow}
        </tbody>
      </table>
      ${buildProjectDescriptionHtml(data.sections?.projectDescription?.fields?.find((f: any) => f.fieldName === 'ProjectDescriptionText')?.value)}
      <h2 class="executive-summary-heading" style="margin-top: 2rem;color: #1976d2 !important; font-weight: bold; margin-bottom: 1rem; font-size: 25px !important;">1. EXECUTIVE SUMMARY</h2>
      <h3 class="executive-summary-subheading" style="font-weight: bold; margin-bottom: 0.5rem; font-size: 23px !important;">1.1. Application for Payment</h3>
      <p>We received a copy of the Application and Certificate for Payment for the following amount.</p>
      ${buildAmountLinesHtml(
        data.coverPage?.budgetRows,
        data.budgetSummary?.requestedAmount,
        contractor,
        contractName,
        application,
        'executiveSummary',
        // 1.1 states what the contractor REQUESTED; the cover letter states what we recommend
        // releasing. The reduction block below then shows both figures side by side.
        'requestedAmount',
      )}
      ${
        hasReductions
          ? Array.isArray(reductions)
            ? buildAllReductionBlocksHtml(reductions)
            : buildExecutiveSummaryReductionsHtml(reductions as ExecSummaryReductions, contractName, escapeHtml(contractor))
          : `<p>In our opinion, and with consideration of the Report below, the amount recommended is comparable to the Work completed on site based upon a Percentage of Completion Method, and the funds remaining in the Hard Cost Budget are adequate to complete the Work.</p>`
      }
    </div>
  `;
};

// Build base Cover Page HTML matching editor defaults
export const buildCoverPageHTML = (data: ReportTemplateData, hasReductions?: boolean): string => {
  // Use report date from Site Conditions (Month Day, Year format); blank if not set
  const reportDateRaw = data.siteConditions?.reportDate;
  const date = reportDateRaw ? formatISODate(reportDateRaw) : '';
  const client = data.projectSummary?.client;
  const projectAddress = data.projectSummary?.projectAddress;
  const rawLoanOfficerName = orEmpty(data.projectSummary?.loanOfficerName);
  const loanOfficerName = rawLoanOfficerName;
  const nameParts = rawLoanOfficerName.split(/\s+/).filter(Boolean);
  const bank = orEmpty(client?.name);
  // "Mr./Ms. LastName" when the Loan Officer name has an honorific; otherwise address the bank team
  const hasHonorific = nameParts.length > 1 && /^(mr|ms)\.?$/i.test(nameParts[0]);
  const greetingName = hasHonorific ? `${nameParts[0].replace(/\.?$/, '.')} ${nameParts.at(-1)}` : bank ? `${bank} Team` : 'Sir or Madam';
  const street = orEmpty(client?.address?.street);
  const city = orEmpty(capitalize(client?.address?.city));
  const stateAbbr = orEmpty(client?.address?.state).toUpperCase();
  const zip = orEmpty(client?.address?.postalCode);
  const projectName = orEmpty(data.projectSummary?.projectTitle);
  const projectLocation = joinNonEmpty([
    projectAddress?.street,
    capitalize(projectAddress?.city),
    joinNonEmpty([orEmpty(projectAddress?.state).toUpperCase(), projectAddress?.postalCode], ' '),
  ]);
  const application = orEmpty(data.drawSummary?.applicationNumber);
  const contractor = orEmpty(data.projectSummary?.contractor);
  const contractName = orEmpty(data.drawSummary?.name);
  const authorName = data.author?.name || 'CDCC Representative';
  const authorEmail = data.author?.email || '';

  const budgetRows = data.coverPage?.budgetRows;
  const amountLinesHtml = buildAmountLinesHtml(
    budgetRows,
    data.budgetSummary?.totalAmountReccForFunding,
    contractor,
    contractName,
    application,
    'coverLetter',
  );
  const percentCompleteLinesHtml = buildPercentCompleteLinesHtml(
    budgetRows,
    data.budgetSummary?.totalContractBudgetPerComplete,
    contractName,
  );

  return `
    <div class="cover-letter" style="font-family: Times New Roman, serif; font-size: 23px;">
      <div style="text-align: center; margin-bottom: 2rem;">
        <img alt="CD Construction Consulting" class="company-logo" />
      </div>
      <p>${escapeHtml(date)}</p>
      <table style="border-collapse: collapse; margin-bottom: 1.5rem; font-family: Times New Roman, serif; font-size: 21px;">
        <tbody>
          <tr style="vertical-align: top;">
            <td style="padding: 0 1.5rem 0.4rem 0; white-space: nowrap; font-size: 21px; font-family: Times New Roman, serif;">Client:</td>
            <td style="padding: 0 0 0 0; font-size: 21px; font-family: Times New Roman, serif;">
              ${[loanOfficerName, bank, street, joinNonEmpty([joinNonEmpty([city, stateAbbr], ', '), zip], ' ')]
                .filter(Boolean)
                .map(escapeHtml)
                .map(line => `<strong>${line}</strong>`)
                .join('<br />')}
            </td>
          </tr>
          <tr style="vertical-align: top;">
            <td style="padding: 0.6rem 1.5rem 0.4rem 0; white-space: nowrap; font-size: 21px; font-family: Times New Roman, serif;">Project:</td>
            <td style="padding: 0.6rem 0 0.4rem 0; font-size: 21px; font-family: Times New Roman, serif;">
              ${[projectName, projectLocation]
                .filter(Boolean)
                .map(escapeHtml)
                .map(line => `<strong>${line}</strong>`)
                .join('<br />')}
            </td>
          </tr>
          <tr style="vertical-align: top;">
            <td style="padding: 0.6rem 1.5rem 0 0; white-space: nowrap; font-size: 21px; font-family: Times New Roman, serif;">Project #</td>
            <td style="padding: 0.6rem 0 0 0; font-size: 21px; font-family: Times New Roman, serif;"><strong>${escapeHtml(orEmpty(data.projectSummary?.projectNumber))}</strong></td>
          </tr>
        </tbody>
      </table>
      <p>Dear ${escapeHtml(greetingName)},</p>
      <p>Attached please find the Construction Monitoring Progress Report for the above referenced project.</p>
      <p>We have reviewed the request for payment this period, and with your approval and acceptance of the total Contract Amount, Schedule of Values, Previous Fundings, Retainage, Soft Costs and Report our recommendation is to release:</p>
      ${amountLinesHtml}
      ${
        hasReductions
          ? `<p><strong>Please note that this amount is less than the amount requested by the Borrower. Refer to the report below for details.</strong></p>`
          : `<p>This is the same amount requested by the Borrower for Construction Hard Costs. Refer to the Executive Summary Action Items noted below in Section 1.2 for any qualifying statements to our recommendation for funding.</p>`
      }
      <p>We estimate the total Hard Cost Contract amount of the project is at least:</p>
      ${percentCompleteLinesHtml}
      <p style="text-align: center; margin: 0;">(based on a Percentage of Completion Method)</p>
      <p style="margin-top: 1rem;">Please email me directly if you have any questions.</p>
      <p>Respectfully,</p>
      ${
        data.author?.signatureUrl
          ? `<img src="${escapeHtml(data.author.signatureUrl)}" alt="Signature" style="height:80px !important; width:auto !important; max-width:260px !important; max-height:80px !important;" />`
          : ''
      }
      <p style="margin-top: 1rem;margin-bottom: 0;">${escapeHtml(authorName)}</p>
      <p class="signature-company" style="margin: 0;">CD Construction Consulting</p>
      ${authorEmail ? `<p style="margin: 0;">${escapeHtml(authorEmail)}</p>` : ''}
    </div>
  `;
};

/**
 * Extracts the site visit value from executive summary HTML content
 * @param executiveSummaryHtml - The executive summary HTML content
 * @returns The extracted site visit value or empty string
 */
export const extractSiteVisitFromExecutiveSummary = (executiveSummaryHtml: string): string => {
  if (!executiveSummaryHtml) return '';

  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = executiveSummaryHtml;

  // Get all text content
  const textContent = tempDiv.textContent || tempDiv.innerText || '';

  // Match "Site Visit Number:" or legacy "Site Visit:" followed by text until we hit a newline or next field name
  const siteVisitMatch = textContent.match(/(?:Site Visit Number|Site Visit):\s*([^\n]+?)(?=\s*(?:Inspection Date|Report Date|\n|$))/i);

  if (siteVisitMatch && siteVisitMatch[1]) {
    return siteVisitMatch[1].trim();
  }

  return '';
};

// Extracts only the inspection date portion from the combined "Site Visit:" cell value
// The cell may contain "#<number>, <date>" or just "<date>" when no site visit number is set
export const extractInspectionDateFromExecutiveSummary = (executiveSummaryHtml: string): string => {
  const combined = extractSiteVisitFromExecutiveSummary(executiveSummaryHtml);
  if (!combined) return '';
  // Strip leading "#<number>, " prefix if present
  return combined.replace(/^#\S+,\s*/, '').trim();
};
